import { useState } from "react";
import { Plus } from "lucide-react";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeliveryNotesTable, DeliveryNoteDialog } from "@/components/DeliveryNotesTable";
import { useSupplierNames } from "@/components/PurchaseOrdersTable";

// Re-exported for the delivery note detail page, which opens the same dialog.
export { DeliveryNoteDialog } from "@/components/DeliveryNotesTable";

const ALL_SUPPLIERS = "all";

export function DeliveryNotesPage({ onSelect }: { onSelect?: (deliveryNoteId: string) => void } = {}) {
  const [createOpen, setCreateOpen] = useState(false);
  // Bumped after a create so the table refetches.
  const [reloadSignal, setReloadSignal] = useState(0);
  const [supplierId, setSupplierId] = useState<string>(ALL_SUPPLIERS);
  const suppliers = useSupplierNames();

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Delivery notes"
        subtitle="Record goods received, then Dolfin reconciles received quantities against the supplier's bills automatically."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            New delivery note
          </Button>
        }
      />

      {/* Only worth showing once there is more than one supplier to tell apart. */}
      {suppliers.size > 1 ? (
        <div className="mb-4 flex justify-end">
          <Select value={supplierId} onValueChange={setSupplierId}>
            <SelectTrigger className="h-9 w-56" aria-label="Filter by supplier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SUPPLIERS}>All suppliers</SelectItem>
              {[...suppliers]
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <DeliveryNotesTable
        onSelect={onSelect}
        reloadSignal={reloadSignal}
        supplierId={supplierId === ALL_SUPPLIERS ? undefined : supplierId}
      />

      <DeliveryNoteDialog
        open={createOpen}
        editing={null}
        onOpenChange={setCreateOpen}
        onSaved={() => setReloadSignal((s) => s + 1)}
      />
    </PageContainer>
  );
}
